export interface SharePointItem {
  Id: number;
  Title: string;
  [key: string]: any;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  listName?: string;
  error?: string;
  message?: string;
}

// All reads/writes go through our own backend (Azure Functions in prod, Express locally), which:
//  1) verifies the caller's delegated token actually has access to the SharePoint site (the "gate check"), then
//  2) performs the real SharePoint call using the backend's own app-only (application permissions) token.
// The user's delegated token is therefore only ever used as proof-of-access, never to talk to SharePoint directly.
class SharePointService {
  private apiBaseUrl = '';
  private accessTokenFallback: (() => Promise<string | null>) | null = null;

  async initialize(_siteUrl: string, getAccessToken: () => Promise<string | null>): Promise<void> {
    this.accessTokenFallback = getAccessToken;

    const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (configuredBaseUrl && configuredBaseUrl.trim().length > 0) {
      this.apiBaseUrl = configuredBaseUrl;
    } else {
      this.apiBaseUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:5000'
        : 'https://timemgmtapi-daabf4g6g5endvek.swedencentral-01.azurewebsites.net';
    }
  }

  private async resolveToken(accessToken?: string): Promise<string> {
    if (accessToken) return accessToken;
    const fallbackToken = await this.accessTokenFallback?.();
    if (fallbackToken) return fallbackToken;
    throw new Error('Ingen åtkomsttoken tillgänglig för SharePoint-anrop');
  }

  private async apiFetch(path: string, accessToken: string | undefined, options: RequestInit = {}): Promise<any> {
    const token = await this.resolveToken(accessToken);
    const response = await fetch(`${this.apiBaseUrl}/api/sharepoint${path}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });

    const text = await response.text();
    const json = text ? JSON.parse(text) : {};

    if (!response.ok) {
      throw new Error(json.message || json.error || `SharePoint API request failed: ${response.status}`);
    }
    return json;
  }

  private listParam(listName: string): string {
    return `list=${encodeURIComponent(listName)}`;
  }

  async getListItems(listName: string, accessToken: string, queryParams?: {
    $filter?: string;
    $select?: string;
    $expand?: string;
    $top?: number;
    $orderby?: string;
  }): Promise<ApiResponse<SharePointItem[]>> {
    try {
      const params = [this.listParam(listName)];
      if (queryParams?.$select) params.push(`$select=${encodeURIComponent(queryParams.$select)}`);
      if (queryParams?.$expand) params.push(`$expand=${encodeURIComponent(queryParams.$expand)}`);
      if (queryParams?.$filter) params.push(`$filter=${encodeURIComponent(queryParams.$filter)}`);
      if (queryParams?.$orderby) params.push(`$orderby=${encodeURIComponent(queryParams.$orderby)}`);
      if (queryParams?.$top) params.push(`$top=${queryParams.$top}`);

      const json = await this.apiFetch(`/list-items?${params.join('&')}`, accessToken);
      const items: any[] = json.data || [];

      return { success: true, data: items, count: items.length, listName };
    } catch (error) {
      console.error('Error fetching list items:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async createListItem(listName: string, itemData: any, accessToken: string): Promise<ApiResponse<SharePointItem>> {
    try {
      const json = await this.apiFetch(`/list-items?${this.listParam(listName)}`, accessToken, {
        method: 'POST',
        body: JSON.stringify(itemData)
      });
      return { success: true, data: json.data, listName };
    } catch (error) {
      console.error('Error creating list item:', error);
      throw error;
    }
  }

  async updateListItem(listName: string, itemId: number, itemData: any, accessToken: string): Promise<ApiResponse<SharePointItem>> {
    try {
      await this.apiFetch(`/list-items/${itemId}?${this.listParam(listName)}`, accessToken, {
        method: 'PATCH',
        body: JSON.stringify(itemData)
      });
      const updatedItem = await this.getListItem(listName, itemId, accessToken);
      return { success: true, data: updatedItem.data, listName };
    } catch (error) {
      console.error('Error updating list item:', error);
      throw error;
    }
  }

  async deleteListItem(listName: string, itemId: number, accessToken: string): Promise<ApiResponse<any>> {
    try {
      await this.apiFetch(`/list-items/${itemId}?${this.listParam(listName)}`, accessToken, { method: 'DELETE' });
      return {
        success: true,
        data: { id: itemId },
        message: 'Item recycled successfully',
        listName
      };
    } catch (error) {
      console.error('Error deleting list item:', error);
      throw error;
    }
  }

  async getItemVersions(listName: string, itemId: number, accessToken: string): Promise<ApiResponse<any[]>> {
    try {
      const json = await this.apiFetch(`/item-versions/${itemId}?${this.listParam(listName)}`, accessToken);
      return { success: true, data: json.data || [], listName };
    } catch (error) {
      console.error('Error fetching item versions:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async getListItem(listName: string, itemId: number, accessToken: string): Promise<ApiResponse<SharePointItem>> {
    try {
      const json = await this.apiFetch(`/list-items/${itemId}?${this.listParam(listName)}`, accessToken);
      return { success: true, data: json.data, listName };
    } catch (error) {
      console.error('Error fetching list item:', error);
      throw error;
    }
  }

  async getListSchema(listName: string, accessToken: string): Promise<ApiResponse<any[]>> {
    try {
      const json = await this.apiFetch(`/list-schema?${this.listParam(listName)}`, accessToken);
      const fields = json.data || [];
      return { success: true, data: fields, count: fields.length, listName };
    } catch (error) {
      console.error('Error fetching list schema:', error);
      throw error;
    }
  }

  async testWritePermissions(accessToken: string): Promise<ApiResponse<any>> {
    try {
      const json = await this.apiFetch(`/test-connection`, accessToken);
      return { success: true, data: { message: 'Connection successful', web: json.data?.siteTitle } };
    } catch (error) {
      console.error('Error testing write permissions:', error);
      throw error;
    }
  }

  async testConnection(accessToken: string): Promise<ApiResponse<any>> {
    try {
      const json = await this.apiFetch(`/test-connection`, accessToken);
      return {
        success: true,
        data: {
          message: 'Connected to SharePoint',
          site: json.data?.siteTitle,
          url: json.data?.siteUrl
        }
      };
    } catch (error) {
      console.error('Error testing connection:', error);
      throw error;
    }
  }

  async getCurrentUser(accessToken: string): Promise<ApiResponse<any>> {
    try {
      const json = await this.apiFetch(`/current-user`, accessToken);
      const user = json.data || {};
      return {
        success: true,
        data: {
          displayName: user.Title,
          email: user.Email,
          loginName: user.LoginName,
          id: user.Id
        }
      };
    } catch (error) {
      console.error('[CurrentUser] Error:', error);
      throw error;
    }
  }

  async listAllLists(accessToken: string): Promise<ApiResponse<any[]>> {
    try {
      const json = await this.apiFetch(`/lists`, accessToken);
      const lists = json.data || [];
      return { success: true, data: lists, count: lists.length };
    } catch (e) {
      console.error('[ListEnum] Error:', e);
      return { success: false, data: [], error: e instanceof Error ? e.message : 'Unknown error' };
    }
  }

  async getUserIdByEmail(email: string): Promise<number | null> {
    try {
      const json = await this.apiFetch(`/site-users?$filter=${encodeURIComponent(`Email eq '${email}'`)}`, undefined);
      const users = json.data || [];
      return users.length > 0 ? users[0].Id : null;
    } catch (error) {
      console.error(`[GetUserId] Error finding user ${email}:`, error);
      return null;
    }
  }

  async getUserIdByLoginName(loginName: string): Promise<number | null> {
    try {
      const json = await this.apiFetch(`/site-users?$filter=${encodeURIComponent(`LoginName eq '${loginName}'`)}`, undefined);
      const users = json.data || [];
      return users.length > 0 ? users[0].Id : null;
    } catch (error) {
      console.error(`[GetUserId] Error finding user ${loginName}:`, error);
      return null;
    }
  }

  async resolvePersonField(personDisplayName: string, personEmail?: string): Promise<number | null> {
    // Först försök med email om tillgängligt
    if (personEmail) {
      const userId = await this.getUserIdByEmail(personEmail);
      if (userId) return userId;
    }

    // Om display name innehåller (@) är det troligtvis email
    if (personDisplayName.includes('@')) {
      const userId = await this.getUserIdByEmail(personDisplayName);
      if (userId) return userId;
    }

    // Försök med display name (Title)
    try {
      const json = await this.apiFetch(`/site-users?$filter=${encodeURIComponent(`Title eq '${personDisplayName}'`)}`, undefined);
      const users = json.data || [];
      if (users.length > 0) return users[0].Id;
    } catch (error) {
      console.error(`[ResolvePerson] Error finding user by display name ${personDisplayName}:`, error);
    }

    console.log(`[ResolvePerson] Could not resolve person: ${personDisplayName}`);
    return null;
  }

  async getAllTenantUsers(): Promise<string[]> {
    try {
      const json = await this.apiFetch(`/site-users?$select=${encodeURIComponent('Title,PrincipalType')}`, undefined);
      const users = json.data || [];
      return users
        .filter((user: any) => user.PrincipalType === 1) // Endast riktiga användare, inte SharePoint-/Säkerhetsgrupper
        .map((user: any) => user.Title)
        .filter((name: string) => name && !name.startsWith('Everyone'))
        .sort((a: string, b: string) => a.localeCompare(b, 'sv'));
    } catch (error) {
      console.error('[GetUsers] Error fetching site users:', error);
      return [];
    }
  }

  async searchTenantUsers(searchTerm: string): Promise<string[]> {
    try {
      const params = [
        `$filter=${encodeURIComponent(`substringof('${searchTerm}', Title)`)}`,
        `$select=${encodeURIComponent('Title,PrincipalType')}`
      ];
      const json = await this.apiFetch(`/site-users?${params.join('&')}`, undefined);
      const users = json.data || [];
      return users
        .filter((user: any) => user.PrincipalType === 1)
        .map((user: any) => user.Title)
        .filter((name: string) => name && !name.startsWith('Everyone'))
        .sort((a: string, b: string) => a.localeCompare(b, 'sv'));
    } catch (error) {
      console.error('[SearchUsers] Error searching users:', error);
      return [];
    }
  }
}

export const sharepointService = new SharePointService();